import { motion } from 'framer-motion';
import heroImg from '../assets/hero-workspace.jpeg';

export default function HeroSection() {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-hermes-800 via-hermes-900 to-warm-900">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-72 h-72 bg-hermes-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-hermes-400/10 rounded-full blur-3xl animate-pulse delay-1000" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-hermes-500/5 rounded-full blur-3xl" />
        {/* Subtle pattern overlay */}
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)',
          backgroundSize: '80px 80px'
        }} />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-6 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left: Text content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="text-left"
          >
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-hermes-400/30 rounded-full px-5 py-2 mb-8">
              <span className="w-2 h-2 rounded-full bg-hermes-400 animate-pulse" />
              <span className="text-warm-200/80 text-sm font-medium">JKDCoding · 24/7 全自動運作</span>
            </div>

            {/* Main headline */}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-black text-warm-100 leading-tight mb-6">
              你的網站，是
              <span className="text-gradient inline-block mx-2">「裝飾品」</span>
              <br className="hidden sm:block" />
              還是
              <span className="text-gradient inline-block mx-2">「自動印鈔機」</span>
              ？
            </h1>

            {/* Subtitle */}
            <p className="text-lg sm:text-xl text-warm-300/70 max-w-xl mb-8 leading-relaxed">
              專為品牌主打造的 24 小時不停運作「自動銷售作業系統」
            </p>

            {/* Value proposition pills */}
            <div className="flex flex-wrap gap-3 mb-10">
              {['門面', '收銀機', '業務員', '會計師', '行銷團隊'].map((item, i) => (
                <motion.span
                  key={item}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5 + i * 0.1 }}
                  className="bg-white/8 backdrop-blur border border-hermes-400/20 text-warm-200 px-4 py-2 rounded-full text-sm font-medium"
                >
                  {item}
                </motion.span>
              ))}
            </div>

            {/* CTA Button */}
            <motion.a
              href="#pain-points"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2 }}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-hermes-500 to-hermes-600 text-white font-bold px-8 py-4 rounded-full text-lg shadow-2xl shadow-hermes-500/20 hover:shadow-hermes-500/40 hover:scale-105 transition-all duration-300"
            >
              了解更多
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </motion.a>

            {/* Target audience */}
            <p className="mt-8 text-warm-400/50 text-sm">
              適用對象：品牌主 · 電商創辦人 · 產品供應商 · 連鎖店經營者
            </p>
          </motion.div>

          {/* Right: Hero Image */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="hidden lg:block"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-hermes-500/20 to-hermes-400/10 rounded-3xl blur-2xl scale-105" />
              <img
                src={heroImg}
                alt="品牌工作美學空間"
                className="relative rounded-3xl shadow-2xl shadow-black/30 w-full h-auto object-cover border border-hermes-400/20"
              />
              {/* Floating stats */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.5 }}
                className="absolute -bottom-4 -left-4 bg-warm-50 rounded-xl px-4 py-3 shadow-xl border border-warm-300"
              >
                <p className="text-xs text-warm-500">月營收增長</p>
                <p className="text-xl font-black text-hermes-500">+110%</p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.8 }}
                className="absolute -top-4 -right-4 bg-warm-50 rounded-xl px-4 py-3 shadow-xl border border-warm-300"
              >
                <p className="text-xs text-warm-500">自動化率</p>
                <p className="text-xl font-black text-hermes-700">95%</p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <div className="w-6 h-10 border-2 border-warm-400/30 rounded-full flex justify-center pt-2">
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-1.5 h-1.5 bg-hermes-400 rounded-full"
          />
        </div>
      </motion.div>
    </section>
  );
}
